import { useState } from "react";

// Candidate-facing list of trusted/known devices with a revoke control.
// Revoking a device signs out any session still bound to it and forces a fresh
// email-OTP challenge the next time that device signs in — the lever a student
// uses to cut off a login they shared.
export default function ManageDevices({ devices = [], csrf_token }) {
  const [list, setList] = useState(devices);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);

  async function revoke(device) {
    const prompt = device.current
      ? "This is the device you're using now — revoking it will sign you out. Continue?"
      : `Remove "${device.label}"? It will need a new email code to sign in again.`;
    if (!window.confirm(prompt)) return;

    setBusyId(device.id);
    setError(null);
    try {
      const res = await fetch(device.destroy_path, {
        method: "DELETE",
        headers: { "X-CSRF-Token": csrf_token, Accept: "application/json" },
      });
      if (!res.ok) {
        setError("Could not remove that device — please try again.");
        return;
      }
      setList((prev) => prev.filter((d) => d.id !== device.id));
      if (device.current) window.location.reload();
    } catch {
      setError("Network error — please try again.");
    } finally {
      setBusyId(null);
    }
  }

  if (list.length === 0) {
    return (
      <div className="device-empty">
        <i className="fa-solid fa-laptop" aria-hidden="true" />
        <p>No active devices on record yet.</p>
      </div>
    );
  }

  return (
    <div data-testid="manage-devices">
      {error && (
        <div className="mv-flash mv-flash--err" role="alert" style={{ marginBottom: 16 }}>
          <span className="mv-flash__icon">
            <i className="fa-solid fa-triangle-exclamation" aria-hidden="true" />
          </span>
          <span className="mv-flash__body">{error}</span>
        </div>
      )}

      <ul className="device-list">
        {list.map((device) => (
          <li key={device.id} className="device-row">
            <span className="device-row__icon" aria-hidden="true">
              <i className={iconFor(device.label)} />
            </span>

            <div className="device-row__main">
              <div className="device-row__name">
                {device.label}
                {device.current && <span className="device-badge --current">This device</span>}
                {!device.trusted && <span className="device-badge --pending">Pending</span>}
              </div>
              <div className="device-row__seen">Last seen {device.last_seen_human} ago</div>
            </div>

            <button
              type="button"
              className="cand-btn --sm"
              disabled={busyId === device.id}
              onClick={() => revoke(device)}
            >
              {busyId === device.id ? "Removing…" : "Remove"}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Pick a Font Awesome glyph from the parsed "Browser on OS" label so each row
// reads at a glance. Falls back to a neutral device icon.
function iconFor(label = "") {
  const l = label.toLowerCase();
  if (l.includes("iphone") || l.includes("android") || l.includes("ios")) return "fa-solid fa-mobile-screen";
  if (l.includes("ipad") || l.includes("tablet")) return "fa-solid fa-tablet-screen-button";
  if (l.includes("chrome")) return "fa-brands fa-chrome";
  if (l.includes("firefox")) return "fa-brands fa-firefox-browser";
  if (l.includes("safari")) return "fa-brands fa-safari";
  if (l.includes("edge")) return "fa-brands fa-edge";
  if (l.includes("windows")) return "fa-brands fa-windows";
  if (l.includes("mac") || l.includes("apple")) return "fa-brands fa-apple";
  if (l.includes("linux")) return "fa-brands fa-linux";
  return "fa-solid fa-laptop";
}
